/**
 * `<ImpactCards>` — the project-home "What shipped, what moved" strip.
 *
 * One card per logged action (brief exported, keyword tracked, audit
 * completed, ...) with Before / +7d / +28d columns computed from any
 * attached outcome checkpoints. Empty / loading / error states are all
 * rendered inline so the section never blanks.
 */
import { CalendarDays, Loader2, Sparkles } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type {
  Action,
  ActionKind,
  ImpactCard,
  OutcomeCheckpoint,
  OutcomeMetric,
} from '@seo/types';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty';
import { ErrorBanner } from '@/components/error-banner';
import { useImpactCards } from '@/features/actions/use-impact-cards';
import { cn } from '@/lib/cn';
import { relTime } from '@/lib/format';

/* ------------------------------------------------------------------ helpers */

const KIND_LABEL: Record<ActionKind, string> = {
  brief_exported: 'Brief exported',
  refresh_started: 'Refresh started',
  keyword_tracked: 'Keyword tracked',
  audit_completed: 'Audit completed',
  audit_fix: 'Audit fix',
};

function subjectLabel(action: Action): string {
  const meta = (action.metadata ?? {}) as Record<string, unknown>;
  // Prefer human-friendly fields the API is likely to include; fall back to
  // subject_type-specific readable defaults so the row is never a bare UUID.
  const label =
    (typeof meta.keyword === 'string' && meta.keyword) ||
    (typeof meta.title === 'string' && meta.title) ||
    (typeof meta.label === 'string' && meta.label) ||
    action.target_url ||
    (action.subject_type === 'audit' ? 'Audit' : null);
  if (label) return label;
  // subject_id may be a URL for `url` subjects — show that, otherwise short uuid.
  if (action.subject_type === 'url') {
    return action.subject_id || 'URL action';
  }
  if (action.subject_type === 'brief') return 'Brief exported';
  return `${action.subject_type} ${action.subject_id.slice(0, 8)}`;
}

interface CheckpointValue {
  position?: number | null;
  clicks_28d?: number | null;
  impressions_28d?: number | null;
  open_criticals?: number | null;
  provider?: string | null;
  source?: string | null;
}

function readValue(cp: OutcomeCheckpoint): CheckpointValue {
  return (cp.value ?? {}) as CheckpointValue;
}

function readBaseline(action: Action): CheckpointValue {
  return (action.baseline ?? {}) as CheckpointValue;
}

function metricForAction(action: Action): OutcomeMetric | null {
  switch (action.kind) {
    case 'audit_completed':
    case 'audit_fix':
      return 'audit_open_criticals';
    case 'brief_exported':
    case 'refresh_started':
      return 'gsc_clicks_impressions';
    case 'keyword_tracked':
      return 'serp_position';
    default:
      return null;
  }
}

function formatProvider(provider?: string | null): string {
  if (!provider) return '';
  if (provider === 'dataforseo') return 'DataForSEO';
  if (provider === 'serper') return 'Serper';
  return provider;
}

/** Compute a delta *rating* against baseline for coloring. */
function ratingFor(
  metric: OutcomeMetric,
  baseline: CheckpointValue,
  current: CheckpointValue,
): 'better' | 'worse' | 'same' | 'unknown' {
  if (metric === 'serp_position') {
    const b = baseline.position ?? null;
    const c = current.position ?? null;
    if (b == null || c == null) return 'unknown';
    if (c < b) return 'better'; // lower position number = better rank
    if (c > b) return 'worse';
    return 'same';
  }
  if (metric === 'gsc_clicks_impressions') {
    const b = baseline.clicks_28d ?? null;
    const c = current.clicks_28d ?? null;
    if (b == null || c == null) return 'unknown';
    if (c > b) return 'better';
    if (c < b) return 'worse';
    return 'same';
  }
  if (metric === 'audit_open_criticals') {
    const b = baseline.open_criticals ?? null;
    const c = current.open_criticals ?? null;
    if (b == null || c == null) return 'unknown';
    if (c < b) return 'better'; // fewer criticals = better
    if (c > b) return 'worse';
    return 'same';
  }
  return 'unknown';
}

const RATING_CLASS: Record<'better' | 'worse' | 'same' | 'unknown', string> = {
  better: 'text-success',
  worse: 'text-destructive',
  same: 'text-foreground',
  unknown: 'text-foreground',
};

function daysFromNow(iso: string): number {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 0;
  const diff = then - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/* ------------------------------------------------------------------ cell */

function BaselineCell({
  metric,
  baseline,
}: {
  metric: OutcomeMetric | null;
  baseline: CheckpointValue;
}): JSX.Element {
  if (metric === 'serp_position') {
    if (baseline.position == null) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium tabular-nums">#{baseline.position}</span>
        {baseline.provider ? (
          <Badge variant="outline" className="w-fit font-normal capitalize">
            {formatProvider(baseline.provider)}
          </Badge>
        ) : null}
      </div>
    );
  }
  if (metric === 'gsc_clicks_impressions') {
    if (baseline.clicks_28d == null && baseline.impressions_28d == null) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <div className="flex flex-col gap-0.5 tabular-nums">
        <span className="font-medium">{baseline.clicks_28d ?? 0} clicks</span>
        <span className="text-xs text-muted-foreground">
          {baseline.impressions_28d ?? 0} impressions
        </span>
      </div>
    );
  }
  if (metric === 'audit_open_criticals') {
    if (baseline.open_criticals == null) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <span className="font-medium tabular-nums">
        {baseline.open_criticals} criticals
      </span>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

function CheckpointCell({
  metric,
  baseline,
  cp,
}: {
  metric: OutcomeMetric | null;
  baseline: CheckpointValue;
  cp: OutcomeCheckpoint | undefined;
}): JSX.Element {
  if (!cp) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (cp.status === 'pending') {
    const inDays = daysFromNow(cp.due_at);
    return (
      <span className="text-muted-foreground">
        {inDays > 0 ? `in ${inDays} day${inDays === 1 ? '' : 's'}` : 'pending'}
      </span>
    );
  }
  if (cp.status === 'skipped') {
    return (
      <span className="text-muted-foreground">
        {cp.warning ?? 'no data'}
      </span>
    );
  }
  // attached
  const value = readValue(cp);
  const rating = metric ? ratingFor(metric, baseline, value) : 'unknown';
  const toneClass = RATING_CLASS[rating];

  if (metric === 'serp_position') {
    if (value.position == null) return <span className="text-muted-foreground">—</span>;
    return (
      <div className="flex flex-col gap-0.5">
        <span className={cn('font-medium tabular-nums', toneClass)}>
          #{value.position}
        </span>
        {value.provider ? (
          <Badge variant="outline" className="w-fit font-normal capitalize">
            {formatProvider(value.provider)}
          </Badge>
        ) : null}
      </div>
    );
  }
  if (metric === 'gsc_clicks_impressions') {
    return (
      <div className="flex flex-col gap-0.5 tabular-nums">
        <span className={cn('font-medium', toneClass)}>
          {value.clicks_28d ?? 0} clicks
        </span>
        <span className="text-xs text-muted-foreground">
          {value.impressions_28d ?? 0} impressions
        </span>
      </div>
    );
  }
  if (metric === 'audit_open_criticals') {
    if (value.open_criticals == null) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <span className={cn('font-medium tabular-nums', toneClass)}>
        {value.open_criticals} criticals
      </span>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

/* ------------------------------------------------------------ back-ref */

/**
 * A6 back-reference: renders a subtle "From calendar: <title>" hint for
 * brief actions that trace back to a `ContentCalendarItem`. Clicking the
 * link jumps to the parent calendar. Rendered as a small muted row so it
 * never crowds the primary metrics.
 */
function CalendarBackReference({
  item,
  className,
}: {
  item: NonNullable<ImpactCard['calendar_item']>;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 text-xs text-muted-foreground',
        className,
      )}
    >
      <CalendarDays className="h-3 w-3" />
      <span>From calendar:</span>
      <Link
        to="/calendars/$calendarId"
        params={{ calendarId: item.calendar_id }}
        className="truncate underline-offset-2 hover:underline"
      >
        {item.title}
      </Link>
    </div>
  );
}

/* ------------------------------------------------------------------ card */

function ImpactCardRow({ card }: { card: ImpactCard }): JSX.Element {
  const { action, outcomes, calendar_item: calendarItem } = card;
  const metric = metricForAction(action);
  const baseline = readBaseline(action);
  const cp7 = outcomes.find((o) => o.offset_days === 7 && (!metric || o.metric === metric));
  const cp28 = outcomes.find((o) => o.offset_days === 28 && (!metric || o.metric === metric));
  const noBaselineSource =
    metric === 'gsc_clicks_impressions' && baseline.source === 'none';
  const baselineEmpty = isBaselineEmpty(metric, baseline);
  const bothPending =
    (!cp7 || cp7.status === 'pending') && (!cp28 || cp28.status === 'pending');

  // Compact rendering when nothing measurable exists yet — avoids showing a
  // 3-column "— / in 7 days / in 28 days" placeholder that reads as noise.
  if (baselineEmpty && bothPending) {
    const nextDue = cp7 ?? cp28;
    const inDays = nextDue ? daysFromNow(nextDue.due_at) : null;
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 shadow-elevation-sm">
        <Badge variant="secondary" className="font-normal">
          {KIND_LABEL[action.kind]}
        </Badge>
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {subjectLabel(action)}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {inDays != null && inDays > 0
            ? `Measuring — first result in ${inDays} day${inDays === 1 ? '' : 's'}`
            : 'Measuring…'}
          {' · '}
          {relTime(action.created_at)}
        </span>
        {calendarItem ? <CalendarBackReference item={calendarItem} /> : null}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-elevation-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="font-normal">
          {KIND_LABEL[action.kind]}
        </Badge>
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {subjectLabel(action)}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {relTime(action.created_at)}
        </span>
      </div>
      {calendarItem ? <CalendarBackReference item={calendarItem} className="mt-1.5" /> : null}
      <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
        <div className="space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Before
          </div>
          <BaselineCell metric={metric} baseline={baseline} />
        </div>
        <div className="space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            +7d
          </div>
          <CheckpointCell metric={metric} baseline={baseline} cp={cp7} />
        </div>
        <div className="space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            +28d
          </div>
          <CheckpointCell metric={metric} baseline={baseline} cp={cp28} />
        </div>
      </div>
      {noBaselineSource ? (
        <p className="mt-2 text-xs text-muted-foreground">
          No target URL for this action — GSC comparison not available.
        </p>
      ) : null}
    </div>
  );
}

function isBaselineEmpty(
  metric: OutcomeMetric | null,
  baseline: CheckpointValue,
): boolean {
  if (metric === 'serp_position') return baseline.position == null;
  if (metric === 'gsc_clicks_impressions') {
    return baseline.clicks_28d == null && baseline.impressions_28d == null;
  }
  if (metric === 'audit_open_criticals') return baseline.open_criticals == null;
  return true;
}

/* ------------------------------------------------------------------ root */

export function ImpactCards({ projectId }: { projectId: string }): JSX.Element {
  const q = useImpactCards(projectId);

  if (q.isPending && !q.data) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading impact cards…
      </div>
    );
  }

  if (q.isError) {
    return <ErrorBanner error={q.error} />;
  }

  const cards = q.data?.cards ?? [];

  if (cards.length === 0) {
    return (
      <EmptyState
        icon={Sparkles}
        title="No actions logged yet"
        description="Track a keyword or export a brief and check back in 7 days — we’ll show how each one moved."
      />
    );
  }

  return (
    <div className="space-y-3">
      {cards.map((card) => (
        <ImpactCardRow key={card.action.id} card={card} />
      ))}
    </div>
  );
}
